import React, { useEffect, useState } from 'react';
import { getDatabase, ref, set, get } from "firebase/database";
import axios from 'axios';

// Constants
const PERPLEXITY_API_KEY = "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";
const SCHEDULED_TIMES = ['08:00', '20:00']; // Run at 8 AM and 8 PM
const EVENING_UPDATE_TIME = '20:00'; // Evening update time for force updates
const FINAL_UPDATE_TIME = '23:00'; // 11 PM - final update time
const LAST_UPDATE_KEY = 'trump_presidency_last_update';
const UPDATE_STATUS_KEY = 'trump_presidency_update_status';
const LAST_UPDATE_DISPLAY_KEY = 'trump_presidency_last_update_display';
const FIREBASE_LAST_UPDATE_PATH = 'meta/lastUpdate';
const MISSING_DAYS_CHECK_KEY = 'trump_presidency_missing_days_check';

// Define the presidency date ranges
const PRESIDENCY_RANGES = [
  // First term: Jan 20, 2017 to Jan 20, 2021
  { start: new Date(2017, 0, 20), end: new Date(2021, 0, 20), term: 'first' },
  // Second term: Jan 20, 2025 to current date
  { start: new Date(2025, 0, 20), end: new Date(), term: 'second' }
];

const DatabaseUpdater = ({ showStatus = false }) => {
  const [status, setStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [batchUpdateProgress, setBatchUpdateProgress] = useState('');
  const [missingDaysCount, setMissingDaysCount] = useState(0);

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Parse date from string YYYY-MM-DD
  const parseDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Generate all dates within presidency ranges
  const generatePresidencyDates = () => {
    const allDates = [];
    
    for (const range of PRESIDENCY_RANGES) {
      let currentDate = new Date(range.start);
      
      while (currentDate <= range.end) {
        allDates.push({
          date: new Date(currentDate),
          dateKey: formatDate(currentDate),
          term: range.term
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }
    
    return allDates;
  };

  // Check if current time is evening update time or later
  const isEveningUpdate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    
    return currentTimeStr >= EVENING_UPDATE_TIME;
  };

  // Check if the last update for today was before 11 PM
  const needsFinalUpdate = (lastUpdateTime, isToday) => {
    if (!isToday) return false;
    return lastUpdateTime < FINAL_UPDATE_TIME;
  };

  // Get all dates between two dates (inclusive)
  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Get the last update date from localStorage
  const getLastUpdateDate = () => {
    const lastUpdateStr = localStorage.getItem(LAST_UPDATE_KEY);
    if (!lastUpdateStr) return null;
    
    try {
      const lastUpdate = JSON.parse(lastUpdateStr);
      return new Date(lastUpdate.date);
    } catch (error) {
      console.error('Error parsing last update date:', error);
      return null;
    }
  };

  // Check if an update is needed and determine if it should be a force update
  const checkUpdateNeeded = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    
    // Get last update info from localStorage
    const lastUpdateStr = localStorage.getItem(LAST_UPDATE_KEY);
    
    if (!lastUpdateStr) {
      return { needsUpdate: true, forceUpdate: isEveningUpdate() };
    }
    
    try {
      const lastUpdate = JSON.parse(lastUpdateStr);
      const lastUpdateDate = new Date(lastUpdate.date);
      const lastUpdateTime = lastUpdate.time;
      
      // If it's a new day, check if we've passed the first scheduled time
      if (formatDate(lastUpdateDate) !== formatDate(now)) {
        const firstScheduledTime = SCHEDULED_TIMES[0];
        if (currentTimeStr >= firstScheduledTime) {
          return { needsUpdate: true, forceUpdate: isEveningUpdate() };
        }
      }
      
      // Check if we've passed a scheduled time since the last update
      for (const scheduledTime of SCHEDULED_TIMES) {
        if (lastUpdateTime < scheduledTime && currentTimeStr >= scheduledTime) {
          // If this is the evening update, it should be a force update
          const shouldForceUpdate = scheduledTime === EVENING_UPDATE_TIME;
          return { needsUpdate: true, forceUpdate: shouldForceUpdate };
        }
      }
      
      return { needsUpdate: false, forceUpdate: false };
    } catch (error) {
      console.error('Error checking last update:', error);
      return { needsUpdate: true, forceUpdate: isEveningUpdate() };
    }
  };

  // Record successful update
  const recordUpdate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    
    const updateRecord = {
      date: now.toISOString(),
      time: currentTimeStr
    };
    
    localStorage.setItem(LAST_UPDATE_KEY, JSON.stringify(updateRecord));
    
    // Format a human-readable date for the Footer component
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
    
    // Store the formatted date for the Footer component
    localStorage.setItem(LAST_UPDATE_DISPLAY_KEY, formattedDate);
    
    // Also update Firebase with the last update time
    try {
      const database = getDatabase();
      const updateRef = ref(database, FIREBASE_LAST_UPDATE_PATH);
      set(updateRef, {
        timestamp: now.toISOString(),
        formatted: formattedDate
      });
    } catch (error) {
      console.error('Error updating last update time in Firebase:', error);
    }
    
    // Dispatch an event to notify other components about the update
    window.dispatchEvent(new StorageEvent('storage', {
      key: LAST_UPDATE_DISPLAY_KEY,
      newValue: formattedDate,
      storageArea: localStorage
    }));
  };

  // Query Perplexity API for information about current day
  const queryPerplexityForDate = async (dateInfo) => {
    const { date, term } = dateInfo;
    const dateFormatted = `${date.toLocaleString('default', { month: 'long' })} ${date.getDate()}, ${date.getFullYear()}`;
    
    setStatus(`Querying Perplexity API for ${dateFormatted}...`);
    
    const termText = term === 'first' ? 'first term' : 'second term';
    
    const prompt = `
      Provide information about what President Donald Trump did on ${dateFormatted} during his ${termText} presidency.
      If there is no notable activity on this day, just say "No notable activities".
      
      Format your response as a JSON object with these properties:
      1. "summary": a short paragraph summarizing the day's activities
      2. "keyEvents": an array of strings, each describing one key event or activity
      3. "tweets": an array of objects, each with a "content" string and "timestamp" string (if available)
      
      The response should only contain the JSON object, nothing else.
    `;

    const requestData = {
      model: 'sonar',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.2,
      max_tokens: 1500,
    };

    try {
      const response = await axios.post('https://api.perplexity.ai/chat/completions', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PERPLEXITY_API_KEY}`
        }
      });

      if (response.data && response.data.choices && response.data.choices[0]) {
        const content = response.data.choices[0].message.content;
        
        try {
          // Try parsing directly
          return JSON.parse(content);
        } catch (parseError) {
          // Try to extract JSON from the text
          const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/) || 
                          content.match(/{[\s\S]*}/);
          
          if (jsonMatch) {
            return JSON.parse(jsonMatch[1] || jsonMatch[0]);
          }
          
          setStatus('Failed to parse API response');
          return null;
        }
      }
      
      setStatus('No valid response from Perplexity API');
      return null;
    } catch (error) {
      console.error('Error querying Perplexity API:', error.message);
      setStatus(`API Error: ${error.message}`);
      return null;
    }
  };

  // Save data to Firebase
  const saveToFirebase = async (dateKey, data, isForceUpdate = false) => {
    try {
      const updateType = isForceUpdate ? 'Force updating' : 'Saving';
      setStatus(`${updateType} data to Firebase for ${dateKey}...`);
      
      const database = getDatabase();
      const active = data && (data.keyEvents?.length > 0 || data.tweets?.length > 0 || 
                            (data.summary && data.summary.toLowerCase() !== 'no notable activities'));
      
      const eventData = {
        active: !!active,
        summary: data && data.summary ? data.summary : 'No notable activities',
        keyEvents: data && data.keyEvents && Array.isArray(data.keyEvents) ? data.keyEvents : [],
        tweets: data && data.tweets && Array.isArray(data.tweets) ? data.tweets : [],
        lastUpdated: new Date().toISOString(),
        forceUpdated: isForceUpdate
      };
      
      // Reference to the specific date in the database
      const dateRef = ref(database, `events/${dateKey}`);
      
      // Save data to Firebase (this will overwrite existing data)
      await set(dateRef, eventData);
      
      const successMessage = isForceUpdate 
        ? `Data for ${dateKey} force updated successfully (overwritten)`
        : `Data for ${dateKey} saved successfully`;
      setStatus(successMessage);
      return true;
    } catch (error) {
      console.error(`Error saving data:`, error);
      setStatus(`Save Error: ${error.message}`);
      return false;
    }
  };

  // Check if the date already exists in Firebase
  const dateExistsInFirebase = async (dateKey) => {
    try {
      const database = getDatabase();
      const dateRef = ref(database, `events/${dateKey}`);
      const snapshot = await get(dateRef);
      return snapshot.exists();
    } catch (error) {
      console.error(`Error checking if date exists:`, error);
      setStatus(`Database Check Error: ${error.message}`);
      return false;
    }
  };

  // Find all missing days in the database
  const findMissingDays = async () => {
    try {
      setStatus('Checking for missing days in database...');
      
      const allPresidencyDates = generatePresidencyDates();
      const missingDates = [];
      
      // Check each date to see if it exists in Firebase
      for (const dateInfo of allPresidencyDates) {
        const exists = await dateExistsInFirebase(dateInfo.dateKey);
        if (!exists) {
          missingDates.push(dateInfo);
        }
        
        // Add a small delay to avoid overwhelming Firebase
        if (missingDates.length % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setMissingDaysCount(missingDates.length);
      
      if (missingDates.length > 0) {
        setStatus(`Found ${missingDates.length} missing days`);
        return missingDates;
      } else {
        setStatus('All presidency days are present in database');
        return [];
      }
    } catch (error) {
      console.error('Error finding missing days:', error);
      setStatus(`Error checking missing days: ${error.message}`);
      return [];
    }
  };

  // Perform batch update for multiple dates
  const performBatchUpdate = async (dates) => {
    if (updating) return;
    
    try {
      setUpdating(true);
      setBatchUpdateProgress(`Starting batch update for ${dates.length} days...`);
      
      // Save the update status to localStorage so other tabs can see it
      localStorage.setItem(UPDATE_STATUS_KEY, JSON.stringify({
        updating: true,
        startTime: new Date().toISOString(),
        batchUpdate: true,
        totalDates: dates.length
      }));

      let successCount = 0;
      let skipCount = 0;
      const today = formatDate(new Date());

      for (let i = 0; i < dates.length; i++) {
        const dateInfo = dates[i];
        const isToday = dateInfo.dateKey === today;
        
        setBatchUpdateProgress(`Processing ${i + 1}/${dates.length}: ${dateInfo.dateKey}`);
        
        // For today, check if we need a final update (if last update wasn't at 11 PM)
        let shouldForceUpdate = false;
        if (isToday) {
          const lastUpdateStr = localStorage.getItem(LAST_UPDATE_KEY);
          if (lastUpdateStr) {
            try {
              const lastUpdate = JSON.parse(lastUpdateStr);
              shouldForceUpdate = needsFinalUpdate(lastUpdate.time, true) || isEveningUpdate();
            } catch (error) {
              shouldForceUpdate = isEveningUpdate();
            }
          } else {
            shouldForceUpdate = isEveningUpdate();
          }
        }
        
        // Check if date already exists (skip check if force update)
        if (!shouldForceUpdate) {
          const exists = await dateExistsInFirebase(dateInfo.dateKey);
          if (exists) {
            skipCount++;
            continue;
          }
        }
        
        // Get data from Perplexity
        const dateData = await queryPerplexityForDate(dateInfo);
        
        // Save to Firebase
        if (dateData) {
          const success = await saveToFirebase(dateInfo.dateKey, dateData, shouldForceUpdate);
          if (success) {
            successCount++;
            // Add a delay between API calls to avoid rate limiting
            if (i < dates.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
        }
      }
      
      setBatchUpdateProgress(`Batch update completed: ${successCount} updated, ${skipCount} skipped`);
      recordUpdate();
      
      // Record that we've checked for missing days
      localStorage.setItem(MISSING_DAYS_CHECK_KEY, new Date().toISOString());
      
    } catch (error) {
      console.error('Error in batch update process:', error);
      setBatchUpdateProgress(`Batch update error: ${error.message}`);
    } finally {
      setUpdating(false);
      localStorage.removeItem(UPDATE_STATUS_KEY);
      setTimeout(() => setBatchUpdateProgress(''), 5000); // Clear progress after 5 seconds
    }
  };

  // Comprehensive check for missing days on app startup
  const checkForMissingDaysOnStartup = async () => {
    // Check if we've already done a missing days check today
    const lastCheckStr = localStorage.getItem(MISSING_DAYS_CHECK_KEY);
    const today = formatDate(new Date());
    
    let shouldCheck = true;
    if (lastCheckStr) {
      try {
        const lastCheckDate = new Date(lastCheckStr);
        if (formatDate(lastCheckDate) === today) {
          shouldCheck = false; // Already checked today
        }
      } catch (error) {
        // If we can't parse the date, do the check anyway
        shouldCheck = true;
      }
    }
    
    if (!shouldCheck) {
      setStatus('Missing days check already completed today');
      return;
    }
    
    // Check if another tab is already updating
    const updateStatusStr = localStorage.getItem(UPDATE_STATUS_KEY);
    if (updateStatusStr) {
      try {
        const updateStatus = JSON.parse(updateStatusStr);
        const startTime = new Date(updateStatus.startTime);
        const now = new Date();
        
        // If update has been running for more than 30 minutes, assume it failed
        if (now - startTime > 30 * 60 * 1000) {
          localStorage.removeItem(UPDATE_STATUS_KEY);
        } else {
          setStatus('Another tab is already updating the database');
          return;
        }
      } catch (error) {
        localStorage.removeItem(UPDATE_STATUS_KEY);
      }
    }
    
    // Find missing days
    const missingDates = await findMissingDays();
    
    if (missingDates.length > 0) {
      setBatchUpdateProgress(`Found ${missingDates.length} missing days. Starting update...`);
      await performBatchUpdate(missingDates);
    }
  };

  // Check and update missing days based on last update
  const checkAndUpdateMissingDays = async () => {
    const lastUpdateDate = getLastUpdateDate();
    const today = new Date();
    
    if (!lastUpdateDate) {
      // If no previous update, check for all missing days
      await checkForMissingDaysOnStartup();
      return;
    }
    
    // Get all dates from last update to today
    const missingDates = getDatesBetween(lastUpdateDate, today);
    
    // Convert to dateInfo objects
    const missingDateInfos = missingDates.map(date => ({
      date: date,
      dateKey: formatDate(date),
      term: date.getFullYear() >= 2025 ? 'second' : 'first'
    }));
    
    // Remove the first date if it's the same as last update date (unless it's today and needs final update)
    if (missingDateInfos.length > 0 && missingDateInfos[0].dateKey === formatDate(lastUpdateDate)) {
      const isLastUpdateToday = formatDate(lastUpdateDate) === formatDate(today);
      if (!isLastUpdateToday) {
        missingDateInfos.shift();
      } else {
        // Check if today needs a final update
        const lastUpdateStr = localStorage.getItem(LAST_UPDATE_KEY);
        if (lastUpdateStr) {
          try {
            const lastUpdate = JSON.parse(lastUpdateStr);
            if (!needsFinalUpdate(lastUpdate.time, true) && !isEveningUpdate()) {
              missingDateInfos.shift(); // Remove today if no final update needed
            }
          } catch (error) {
            // Keep today in the list if we can't parse the last update
          }
        }
      }
    }
    
    if (missingDateInfos.length > 0) {
      setBatchUpdateProgress(`Found ${missingDateInfos.length} days to update since last update`);
      await performBatchUpdate(missingDateInfos);
    } else {
      setStatus('All days are up to date');
    }
  };

  // Perform the update for today or a specific date
  const updateData = async (customDate = null, forceUpdate = false) => {
    if (updating) return;
    
    try {
      setUpdating(true);
      setStatus('Starting update process...');
      
      // Save the update status to localStorage so other tabs can see it
      localStorage.setItem(UPDATE_STATUS_KEY, JSON.stringify({
        updating: true,
        startTime: new Date().toISOString()
      }));
      
      const targetDate = customDate || new Date();
      const dateKey = formatDate(targetDate);
      const term = targetDate.getFullYear() >= 2025 ? 'second' : 'first';
      const dateInfo = { date: targetDate, dateKey, term };
      
      // For evening updates or explicit force updates, always overwrite
      const shouldForceUpdate = forceUpdate || isEveningUpdate();
      
      // Check if date already exists in Firebase (skip check if this is a force update)
      if (!shouldForceUpdate) {
        const exists = await dateExistsInFirebase(dateKey);
        if (exists) {
          setStatus(`Data for ${dateKey} already exists. No update needed.`);
          recordUpdate();
          setUpdating(false);
          localStorage.removeItem(UPDATE_STATUS_KEY);
          return;
        }
      } else {
        setStatus(`Force update enabled. Updating data for ${dateKey} regardless of existing entries.`);
      }
      
      // Get data from Perplexity
      const dateData = await queryPerplexityForDate(dateInfo);
      
      // Save to Firebase
      if (dateData) {
        const success = await saveToFirebase(dateKey, dateData, shouldForceUpdate);
        if (success) {
          recordUpdate();
          const updateType = shouldForceUpdate ? 'force update' : 'update';
          setStatus(`${updateType.charAt(0).toUpperCase() + updateType.slice(1)} completed successfully at ${new Date().toLocaleTimeString()}`);
        } else {
          setStatus('Update failed. Will try again later.');
        }
      } else {
        setStatus('Failed to get data. Will try again later.');
      }
    } catch (error) {
      console.error('Error in update process:', error);
      setStatus(`Update Error: ${error.message}`);
    } finally {
      setUpdating(false);
      localStorage.removeItem(UPDATE_STATUS_KEY);
    }
  };

  // Perform the update for today (wrapper function for backward compatibility)
  const updateTodayData = async (forceUpdate = false) => {
    await updateData(null, forceUpdate);
  };

  // Force update today's data
  const forceUpdateTodayData = async () => {
    await updateTodayData(true);
  };

  // Check for updates periodically
  useEffect(() => {
    // Initial comprehensive check when component mounts
    const performInitialCheck = async () => {
      try {
        setStatus('Performing initial database check...');
        
        // First, check for missing days across entire presidency
        await checkForMissingDaysOnStartup();
        
        // Then check for regular scheduled updates
        const updateCheck = checkUpdateNeeded();
        if (updateCheck.needsUpdate) {
          await updateTodayData(updateCheck.forceUpdate);
        }
      } catch (error) {
        console.error('Error in initial check:', error);
        setStatus(`Initial check error: ${error.message}`);
      }
    };
    
    // Run initial check
    performInitialCheck();
    
    // Set up interval to check every 15 minutes for regular updates
    const interval = setInterval(() => {
      const updateCheck = checkUpdateNeeded();
      if (updateCheck.needsUpdate) {
        updateTodayData(updateCheck.forceUpdate);
      }
    }, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Only render status if showStatus is true
  if (!showStatus) return null;

  return (
    <div className="database-updater-status">
      <h3>Database Update Status</h3>
      <p>{status || 'Idle'}</p>
      {batchUpdateProgress && <p><strong>Batch Update:</strong> {batchUpdateProgress}</p>}
      {missingDaysCount > 0 && <p><strong>Missing Days Found:</strong> {missingDaysCount}</p>}
      <p>Scheduled updates: {SCHEDULED_TIMES[0]} (regular), {SCHEDULED_TIMES[1]} (force update - overwrites existing data)</p>
      <p>Final update check: {FINAL_UPDATE_TIME} (11 PM - ensures current day is complete)</p>
      <div className="button-container">
        <button 
          onClick={() => updateTodayData(false)} 
          disabled={updating}
          className="update-button"
        >
          {updating ? 'Updating...' : 'Update Today'}
        </button>
        <button 
          onClick={forceUpdateTodayData} 
          disabled={updating}
          className="force-update-button"
        >
          Force Update Today
        </button>
        <button 
          onClick={checkAndUpdateMissingDays} 
          disabled={updating}
          className="batch-update-button"
        >
          Check Missing Days
        </button>
        <button 
          onClick={checkForMissingDaysOnStartup} 
          disabled={updating}
          className="comprehensive-check-button"
        >
          Full Database Check
        </button>
      </div>
    </div>
  );
};

export default DatabaseUpdater;